export const studentDetailVerificationFields = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'otherName', label: 'Other Name' },
    { key: 'dob', label: 'Date of Birth' },
    { key: 'streetNumberPostal', label: 'Postal Street Number' },
    { key: 'streetNamePostal', label: 'Postal Street Name' },
    { key: 'suburbPostal', label: 'Postal Suburb' },
    { key: 'statePostal', label: 'Postal State' },
    { key: 'postcodePostal', label: 'Postal Postcode' },
]

export const createStudentDetailVerificationState = () => (
    studentDetailVerificationFields.reduce((acc, field) => ({
        ...acc,
        [field.key]: {
            checked: false,
            checkedAt: null,
        }
    }), {})
)

export const buildStudentDetailFieldValues = ({
    firstName,
    lastName,
    otherName,
    email,
    dob,
    streetNumber,
    streetName,
    suburb,
    state,
    postcode,
    streetNumberPostal,
    streetNamePostal,
    suburbPostal,
    statePostal,
    postcodePostal,
}) => ({
    firstName,
    lastName,
    otherName,
    email,
    dob,
    streetNumber,
    streetName,
    suburb,
    state: state?.value || state || '',
    postcode,
    streetNumberPostal,
    streetNamePostal,
    suburbPostal,
    statePostal: statePostal?.value || statePostal || '',
    postcodePostal,
})

export const buildStudentDetailVerificationSnapshot = (fieldValues) => (
    JSON.stringify(studentDetailVerificationFields.map(({ key }) => ({
        key,
        value: fieldValues[key] || '',
    })))
)

export const buildStudentDetailVerificationAudit = ({
    existingVerification,
    confirmationState,
    fieldValues,
    actorName = 'Student',
}) => {
    const completedAt = new Date().toISOString()

    return {
        ...(existingVerification || {}),
        student: {
            actorName,
            completedAt,
            fields: studentDetailVerificationFields.reduce((acc, field) => ({
                ...acc,
                [field.key]: {
                    label: field.label,
                    value: fieldValues[field.key] || '',
                    checked: !!confirmationState[field.key]?.checked,
                    checkedAt: confirmationState[field.key]?.checkedAt || null,
                }
            }), {})
        }
    }
}

export const getVerificationActorName = (userData) => {
    const fullName = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim()

    return (
        userData?.name ||
        userData?.displayName ||
        userData?.fullName ||
        fullName ||
        userData?.username ||
        userData?.email ||
        userData?.trainerId ||
        'Trainer'
    )
}

export const getTrainerVerificationFields = (data) => (
    data?.studentDetailVerification?.trainer?.fields || {}
)

export const isTrainerVerificationComplete = (data) => (
    studentDetailVerificationFields.every(({ key }) => !!getTrainerVerificationFields(data)[key]?.checked)
)

export const applyTrainerVerificationUpdate = ({
    currentEoi,
    fieldKey,
    checked,
    actorName,
    actorId,
    fieldValue,
    fieldLabel,
}) => {
    if (!currentEoi) {
        return currentEoi
    }

    const now = new Date().toISOString()
    const currentData = currentEoi.data || {}
    const currentVerification = currentData.studentDetailVerification || {}
    const currentTrainer = currentVerification.trainer || {}
    const currentFields = currentTrainer.fields || {}
    const currentField = currentFields[fieldKey] || {}

    const nextFields = {
        ...currentFields,
        [fieldKey]: {
            ...currentField,
            label: fieldLabel || currentField.label || fieldKey,
            value: fieldValue || '',
            checked: !!checked,
            checkedAt: checked ? now : null,
            checkedBy: checked ? actorName || 'Trainer' : '',
            checkedById: checked ? actorId || '' : '',
            history: [
                ...(currentField.history || []),
                {
                    action: checked ? 'checked' : 'unchecked',
                    at: now,
                    actorName: actorName || 'Trainer',
                    actorId: actorId || '',
                    value: fieldValue || '',
                }
            ]
        }
    }

    const nextData = {
        ...currentData,
        studentDetailVerification: {
            ...currentVerification,
            trainer: {
                ...currentTrainer,
                actorName: actorName || 'Trainer',
                actorId: actorId || '',
                lastUpdatedAt: now,
                fields: nextFields,
            }
        }
    }

    const trainerVerificationComplete = isTrainerVerificationComplete(nextData)

    return {
        ...currentEoi,
        data: nextData,
        trainerVerificationComplete,
        verified: trainerVerificationComplete ? currentEoi.verified : false,
        updated: now,
    }
}

export const buildVerificationAuditEvents = (data) => {
    const verification = data?.studentDetailVerification || {}
    const events = []

    const addEvents = (sourceKey, actorFallback) => {
        const fields = verification?.[sourceKey]?.fields || {}

        Object.entries(fields).forEach(([fieldKey, fieldData]) => {
            ;(fieldData.history || []).forEach((historyItem, index) => {
                events.push({
                    id: `${sourceKey}-${fieldKey}-${historyItem.at || 'na'}-${index}`,
                    source: sourceKey,
                    fieldKey,
                    label: fieldData.label || fieldKey,
                    value: historyItem.value ?? fieldData.value ?? '',
                    action: historyItem.action || 'checked',
                    at: historyItem.at || fieldData.checkedAt || '',
                    actorName: historyItem.actorName || fieldData.checkedBy || verification?.[sourceKey]?.actorName || actorFallback,
                })
            })
        })
    }

    addEvents('student', 'Student')
    addEvents('trainer', 'Trainer')

    ;(data?.changeAuditHistory || []).forEach((event, index) => {
        events.push({
            id: event.id || `change-audit-${event.at || 'na'}-${index}`,
            type: 'record_update',
            at: event.at,
            actorName: event.actorName || 'User',
            changes: event.changes || [],
        })
    })

    return events
        .filter((event) => event.at)
        .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
}

const normalizeAuditValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return ''
    }

    if (Array.isArray(value) || typeof value === 'object') {
        return JSON.stringify(value)
    }

    return String(value)
}

export const buildUpdateAuditEvent = ({ previousData, nextData, actorName, actorId }) => {
    const ignoredKeys = ['changeAuditHistory']
    const allKeys = Array.from(new Set([
        ...Object.keys(previousData || {}),
        ...Object.keys(nextData || {}),
    ])).filter((key) => !ignoredKeys.includes(key))

    const changes = allKeys.reduce((acc, key) => {
        const fromValue = normalizeAuditValue(previousData?.[key])
        const toValue = normalizeAuditValue(nextData?.[key])

        if (fromValue === toValue) {
            return acc
        }

        const label = studentDetailVerificationFields.find((field) => field.key === key)?.label || key

        return [
            ...acc,
            {
                fieldKey: key,
                label,
                fromValue,
                toValue,
            }
        ]
    }, [])

    if (changes.length === 0) {
        return null
    }

    return {
        id: `update-${new Date().toISOString()}`,
        type: 'record_update',
        at: new Date().toISOString(),
        actorName,
        actorId,
        changes,
    }
}
